import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { authFetch } from '../lib/authFetch';
import { LayoutDashboard, ListChecks, CalendarClock, Truck, Building2, Briefcase, MessageSquare } from 'lucide-react';

/**
 * Dispatch nav bar with numeric badges. Renders horizontally scrollable on
 * mobile and inline on desktop. Badge counts poll every 60 s.
 */
export default function DispatchNav() {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await authFetch('/api/dispatch/inquiries/counts');
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setCounts(data);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const items = [
    { to: '/dispatch/dashboard', label: 'Overview', icon: LayoutDashboard, testid: 'nav-overview', badge: null },
    { to: '/dispatch/bookings', label: 'Transportation Requests', icon: ListChecks, testid: 'nav-bookings', badge: null },
    { to: '/dispatch/bookings?filter=today', label: "Today's Dispatch", icon: CalendarClock, testid: 'nav-today', badge: null },
    { to: '/dispatch/drivers', label: 'Drivers', icon: Truck, testid: 'nav-drivers', badge: null },
    { to: '/dispatch/inquiries/vendor', label: 'Vendor Partnerships', icon: Building2, testid: 'nav-vendor', badge: counts?.vendor?.open },
    { to: '/dispatch/inquiries/job', label: 'Job Applications', icon: Briefcase, testid: 'nav-jobs', badge: counts?.job?.open },
    { to: '/dispatch/inquiries/contact', label: 'Contact Inquiries', icon: MessageSquare, testid: 'nav-contact', badge: counts?.contact?.open },
  ];

  return (
    <nav
      aria-label="Dispatch sections"
      data-testid="dispatch-nav"
      className="bg-white border-b border-gray-200 sticky top-[52px] sm:top-[60px] z-10"
    >
      <div className="max-w-6xl mx-auto px-2 sm:px-6">
        <ul className="flex overflow-x-auto no-scrollbar gap-1 py-2">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <li key={it.to} className="flex-shrink-0">
                <NavLink
                  to={it.to}
                  data-testid={it.testid}
                  end={it.to === '/dispatch/dashboard'}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] sm:text-[13px] font-medium border transition-colors ${
                      isActive
                        ? 'bg-[#004C97] text-white border-[#004C97]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-[#004C97] hover:text-[#004C97]'
                    }`
                  }
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="whitespace-nowrap">{it.label}</span>
                  {typeof it.badge === 'number' && it.badge > 0 && (
                    <span
                      data-testid={`${it.testid}-badge`}
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#F9A825] text-white text-[10px] font-bold"
                    >
                      {it.badge}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
